import Head from 'next/head'
import { NextSeo } from 'next-seo'
import Link from 'next/link'
import { Title } from '@mantine/core'

import * as Types from '../declarations'
import { withVersion } from 'lib/withVersion'
import { getDefaultImage } from 'lib/helper'

import PageTemplate from '../components/PageTemplate'
import EmailAddress from '../components/EmailAddress'

const About = ({ version }: { version: Types.VERSION }) => {
	const seo = {
		title: `About - ${
			version === Types.VERSION.TREKKING ? 'Guided Trekking' : 'Guided Peaks'
		}`,
		description:
			version === Types.VERSION.TREKKING
				? 'Learn about Guided Trekking, a platform created to help hikers find guides, discover treks, and plan their next trekking adventures with ease.'
				: 'Learn about Guided Peaks, a platform created to help climbers find guides, discover mountains, and plan their next mountaineering adventures with ease.',
		siteName:
			version === Types.VERSION.TREKKING ? 'Guided Trekking' : 'Guided Peaks',
		canonical: `https://www.guided${
			version === Types.VERSION.TREKKING ? 'trekking' : 'peaks'
		}.com/about`,
		images: [
			{
				url: getDefaultImage(version),
				type: 'image/jpeg',
			},
		],
	}

	return (
		<>
			<Head>
				<title>{seo.title}</title>
			</Head>
			<NextSeo
				title={seo.title}
				description={seo.description}
				canonical={seo.canonical}
				openGraph={{
					url: seo.canonical,
					title: seo.title,
					description: seo.description,
					images: seo.images,
					siteName: seo.siteName,
					type: 'website',
					locale: 'en_US',
				}}
			/>
			<PageTemplate>
				<Title order={1} mb="lg">
					About
				</Title>

				{version === Types.VERSION.TREKKING && (
					<>
						<p>
							I started this site as a long time hiker who loves going on longer
							treks. Spending at least a week, ideally 2-3+ just walking.
						</p>

						<p>
							I wanted to make a website that collected information on many
							beautiful treks around the world. For like minded people to share
							with each other, and also to discover new routes.
						</p>

						<p>
							Likewise, as an easy way to find guides for routes, gathered in
							one place.
						</p>
					</>
				)}
				{version === Types.VERSION.PEAKS && (
					<>
						<p>
							I&apos;ve been in the mountains all my life, but it took a while
							until I climbed my first 6000 meter mountain.
						</p>

						<p>
							At first I was very happy not to go near a mountain for about two
							weeks. But slowly I was thinking of another climb.
						</p>

						<p>But which?</p>

						<p>
							That&apos;s where the idea for this site came from. Slowly I
							decided on a mountain (Mt Elbrus), but still it took half a
							morning of googling around to decide on:
						</p>

						<ul>
							<li>what route to do (if there even were multiple)</li>
							<li>what time of year is it climbed</li>
							<li>what gear would I need, what I already had or much more</li>
							<li>were permits required?</li>
							<li>
								of all the tour companies, who were legit and well reviewed
							</li>
							<li>what would it all realistically cost</li>
							<li>how long would it take</li>
						</ul>

						<p>
							All this info is easily found online, but I wanted a better way -
							with everything gathered in one place.
						</p>

						<p>
							As I&apos;ve travelled around the world mountaineering, there are
							some common experiences to finding a guide. There are guide
							agencies/companies with nice websites (the kind you find on the
							first page of google), and then there are less sophisticated but
							equally capable guides who just have a small store front - if
							that.
						</p>

						<p>
							Usually you&apos;ll get basically the same trip from either guide,
							but with a gigantic price difference. For example, I first climbed
							Island peak in Nepal for a few hundred dollars, but encountered
							others paying $1k or $2k and saw trips online going for $3k or $4k
							and upwards.
						</p>

						<p>
							The goal with Guided Peaks is to open up the market of guides, to
							help climbers find a better deal and likewise let guides find more
							clients. Win win.
						</p>

						<p>So Guided Peaks serves three goals:</p>

						<ul>
							<li>help you discover mountains</li>
							<li>
								give you all the information in one place to decide if a
								mountain is for you
							</li>
							<li>connect you to guides</li>
						</ul>
					</>
				)}

				<Title order={2} mb="lg" mt="xl">
					Contact
				</Title>
				<p>
					If you have any questions/feedback about the site, as a guide or{' '}
					{version === Types.VERSION.TREKKING ? 'trekker' : 'climber'} get in
					touch: <EmailAddress version={version} />
				</p>

				<Title order={2} mb="lg" mt="xl">
					Other
				</Title>
				<Link href="/links-resources">Links</Link>
			</PageTemplate>
		</>
	)
}

export const getStaticProps = withVersion(
	async (context: { version?: Types.VERSION }) => {
		return { props: { version: context?.version } }
	},
)

export default About
